import { useEffect, useRef } from 'react';

import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import { useGamecastCursor } from '@components/VideoGameCast/context/context';
import { default as mytaverseLogger } from '@providers/MytaverseProvider/logger';
import {
  LastWebsocketJsonMessageType,
  WebsocketAction,
} from '@interfaces/webSocketConnectionInfo';

/**
 * Shape every value in UE's cursor vocabulary has: lowercase CSS keywords,
 * hyphen-separated (`pointer`, `not-allowed`, `nwse-resize`).
 *
 * Matching the shape rather than the thirteen known keywords keeps the
 * vocabulary open — UE can author a new state without a web release — while
 * still refusing anything that is not a bare keyword. That refusal is the
 * point: the value is assigned to an inline style, so `url(https://…), auto`
 * would otherwise make the browser fetch a remote cursor image.
 */
const CURSOR_KEYWORD_PATTERN = /^[a-z]+(-[a-z]+)*$/;

/** What an unrecognisable state falls back to, per the UE contract. */
const FALLBACK_CURSOR = 'default';

/** Cap on the rejected state quoted in the log — it is free-form off the wire. */
const MAX_LOGGED_STATE_LENGTH = 40;

/**
 * Turns a reported cursor state into the CSS value to apply.
 *
 * @param state - `cursor.state` off the wire; empty or absent when UE said
 * nothing about the cursor in this message.
 * @returns The CSS `cursor` value, or `null` to leave the current one alone.
 */
export const resolveCursorStyle = (
  state: string | undefined,
): string | null => {
  if (!state) {
    return null;
  }

  return CURSOR_KEYWORD_PATTERN.test(state) ? state : FALLBACK_CURSOR;
};

/**
 * MV-1820 — applies the cursor shape UE reports for the streamed viewport.
 *
 * The stream is a video, so the browser cannot see that the pointer is over a
 * button in the 3D world; UE says so instead, over two messages carrying the
 * same `cursor` object. `SYNC_DATA_UE` is the 1 Hz snapshot — the only
 * synchroniser, and what recovers the state after a reconnect.
 * `CHANGE_CURSOR_UE` is the change event, rate-limited to 10 Hz, and is what
 * makes hover feedback feel immediate instead of up to a second late. They are
 * sampled from the same source at send time and cannot contradict each other,
 * so the last one to arrive wins.
 *
 * A message with no `cursor` field leaves the cursor untouched: the field is
 * absent for a whole session when the `CURSOR_STATE_UE` flag is off or the
 * client predates the feature, and resetting on those would flicker the cursor
 * on every heartbeat.
 *
 * @param lastJsonMessage - Latest message off the dashboard websocket; any
 * other action, or a stale stream session's key, is ignored.
 */
export const useHandleCursorStateMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { isDebugMode } = useMytaverse();
  const { gameliftStreamsSessionKey } = useMytaverseEvent();
  const setCursor = useGamecastCursor((state) => state.setCursor);

  // One log per distinct offending value, not one per message: a UE build that
  // reports a malformed state reports it at up to 10 Hz.
  const reportedInvalidStatesRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, sessionKey, cursor } = lastJsonMessage;

    if (
      action !== WebsocketAction.SyncDataUe &&
      action !== WebsocketAction.ChangeCursorUe
    ) {
      return;
    }

    if (
      sessionKey &&
      sessionKey !== gameliftStreamsSessionKey &&
      !isDebugMode
    ) {
      return mytaverseLogger.debug(
        `Skip handle WS message for sessionKey: ${sessionKey}, expected sessionKey: ${gameliftStreamsSessionKey}`,
      );
    }

    const nextCursor = resolveCursorStyle(cursor?.state);

    if (!nextCursor) {
      return;
    }

    if (
      nextCursor !== cursor?.state &&
      cursor?.state &&
      !reportedInvalidStatesRef.current.has(cursor.state)
    ) {
      reportedInvalidStatesRef.current.add(cursor.state);
      mytaverseLogger.error(
        `Unusable cursor state from UE, falling back to ${FALLBACK_CURSOR}`,
        {
          sessionKey: gameliftStreamsSessionKey,
          state: cursor.state.slice(0, MAX_LOGGED_STATE_LENGTH),
        },
      );
    }

    setCursor(nextCursor);
  }, [lastJsonMessage, gameliftStreamsSessionKey, isDebugMode, setCursor]);
};
